import * as React from 'react';
import {SetRequired} from 'type-fest';

import {tokens} from '@packages/utilities/styled';
import {defaultRangeExtractor, type Range} from '@tanstack/react-virtual';
import {useVirtualizer} from '@packages/systems/spring-hooks';

import {isOptionGroup} from '../../../hooks';

import {SearchableListItemWrapper} from '../SearchableListItem';
import {useSearchableListBox} from '../useSearchableListBox';
import {useSearchableListContext} from '../useSearchableList';

import {
  SearchableListBoxView,
  SearchableListItemGroupView,
} from './SearchableListBox.styles';
import SearchableListEmptyState from './SearchableListEmptyState';
import {SearchableListLabel} from './SearchableListBoxLabel';

import type {SearchableListBoxProps, SearchableListGroupProps} from './_types';

const VirtualizedSearchableListGroup = ({
  density,
  label,
  hasGroupDividers,
  isOverscan,
  measureElement,
  options,
  renderOption,
  scrollMargin,
  shouldHideLabel,
  verticalItemAlignment,
  virtualIndex,
}: SearchableListGroupProps & {
  hasGroupDividers: boolean;
  isOverscan: boolean;
  measureElement: ReturnType<typeof useVirtualizer>['measureElement'];
  scrollMargin: number;
  virtualIndex: number;
}) => {
  const groupLabelId = React.useId();
  const {refs} = useSearchableListContext();
  const [paddingStart, setPaddingStart] = React.useState(0);

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => refs.floating.current,
    estimateSize: () => 32,
    scrollMargin,
    paddingStart,
    paddingEnd: hasGroupDividers ? 8 : 0,
    overscan: isOverscan ? 0 : 25, // limit overscan on groups that are themselves overscanned.
    indexAttribute: 'data-virtual-index',
  });

  const labelCallbackRef: React.RefCallback<HTMLElement> = React.useCallback(
    (node: HTMLElement) => {
      setPaddingStart(node?.offsetHeight ?? 0);
    },
    []
  );

  const virtualOptions = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const groupStyle = React.useMemo(() => {
    return {
      height: totalSize,
      position: 'relative',
    } as const;
  }, [totalSize]);

  return (
    <SearchableListItemGroupView
      aria-labelledby={groupLabelId}
      data-virtual-index={virtualIndex}
      ref={measureElement}
      role="group"
      style={groupStyle}
    >
      <SearchableListLabel
        id={groupLabelId}
        isVisuallyHidden={shouldHideLabel}
        ref={labelCallbackRef}
      >
        {label}
      </SearchableListLabel>
      {virtualOptions.map((row) => {
        const option = options[row.index];
        if (!option) return null;

        return (
          <div
            key={row.index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${row.start - scrollMargin}px)`,
            }}
          >
            <SearchableListItemWrapper
              categoryLabel={label}
              density={density}
              measureElement={virtualizer.measureElement}
              option={option}
              renderOption={renderOption}
              verticalItemAlignment={verticalItemAlignment}
              virtualIndex={row.index}
            />
          </div>
        );
      })}
    </SearchableListItemGroupView>
  );
};

export const VirtualizedSearchableListBox = React.forwardRef<
  HTMLDivElement,
  SetRequired<SearchableListBoxProps, 'maxHeight'>
>(
  (
    {
      density = 'compact',
      hasGroupDividers = false,
      maxHeight,
      verticalItemAlignment = 'top',
      ...props
    }: SearchableListBoxProps,
    ref
  ) => {
    const [isAtScrollTop, setIsAtScrollTop] = React.useState(true);

    const handleScroll = React.useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        setIsAtScrollTop(event.currentTarget.scrollTop === 0);
      },
      []
    );

    const {
      mergedProps,
      options: optionsProp,
      renderOption,
      refs,
    } = useSearchableListBox({
      ref,
      ...props,
    });

    const visibleRangeRef = React.useRef<[number, number]>([0, 0]);

    /**
     * Grab the indices of the grouped options, so we can always render their wrappers.
     * This lets use preserve their item height within the virtualizer.
     */
    const groupIndices = React.useMemo(() => {
      const acc: number[] = [];

      optionsProp.forEach((option, i) => {
        if (isOptionGroup(option)) {
          acc.push(i);
        }
      });

      return acc;
    }, [optionsProp]);

    const rangeExtractor = React.useCallback(
      (range: Range) => {
        /**
         * We need to keep our grouped options in the range, so we can render their wrappers.
         * These wrappers are used to preserve space and prevent scroll jumping and weird layout issues.
         */
        const next = new Set([
          ...groupIndices,
          ...defaultRangeExtractor(range),
        ]);
        visibleRangeRef.current = [range.startIndex, range.endIndex];

        return [...next].sort((indexA, indexB) => indexA - indexB);
      },
      [groupIndices]
    );

    const count = optionsProp.length;
    const virtualizer = useVirtualizer({
      count,
      getScrollElement: () => refs.floating.current,
      estimateSize: () => 32,
      rangeExtractor,
      overscan: 25,
      indexAttribute: 'data-virtual-index',
    });

    const options = virtualizer.getVirtualItems();

    return (
      <SearchableListBoxView
        hasGroupDividers={hasGroupDividers}
        isVirtualized
        maxHeight={maxHeight}
        onScroll={handleScroll}
        shouldRenderTopShadow={!isAtScrollTop}
        {...mergedProps}
        data-virtual-scroll-container
      >
        {optionsProp.length ? (
          <>
            {/**
             * This div lets the scroll bar represent the true height of the scrollable area.
             *
             * If we don't have this, the element scroll bar appears to be at the end, even though there is still more to show.
             */}
            <div
              style={{
                height: virtualizer.getTotalSize(),
              }}
            >
              {options.map((option) => {
                const row = optionsProp[option.index];
                if (!row) {
                  return null;
                }

                if (isOptionGroup(row)) {
                  const {
                    options: groupedOptions,
                    label,
                    shouldHideGroupLabel,
                  } = row;
                  if (!groupedOptions.length) {
                    return null;
                  }

                  return (
                    <VirtualizedSearchableListGroup
                      density={density}
                      hasGroupDividers={hasGroupDividers}
                      isOverscan={
                        option.index < visibleRangeRef?.current[0] - 1 ||
                        option.index > visibleRangeRef?.current[1] + 1
                      }
                      key={`${label}-${option.index}`}
                      label={label}
                      measureElement={virtualizer.measureElement}
                      options={groupedOptions}
                      renderOption={renderOption}
                      scrollMargin={option.start}
                      shouldHideLabel={shouldHideGroupLabel}
                      verticalItemAlignment={verticalItemAlignment}
                      virtualIndex={option.index}
                    />
                  );
                }

                /**
                 * These ungrouped items occur after any group with a divider
                 * We need to shift them down so they aren't too close to the last border.
                 */
                const isAfterGroupsWithDividers =
                  hasGroupDividers &&
                  typeof groupIndices[0] === 'number' &&
                  option.index > groupIndices[0];

                const transform = isAfterGroupsWithDividers
                  ? `translateY(calc(${option.start}px + ${tokens.ui.rowItem.padding.comfortable.vertical} + ${tokens.space[1]})`
                  : `translateY(${option.start}px)`;

                return (
                  <div
                    key={option.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform,
                    }}
                  >
                    <SearchableListItemWrapper
                      density={density}
                      measureElement={virtualizer.measureElement}
                      option={row}
                      renderOption={renderOption}
                      verticalItemAlignment={verticalItemAlignment}
                      virtualIndex={option.index}
                    />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <SearchableListEmptyState density={density} />
        )}
      </SearchableListBoxView>
    );
  }
);

VirtualizedSearchableListBox.displayName = 'VirtualizedSearchableListBox';

export default VirtualizedSearchableListBox;
